import { CUSTOM_SCHEDULE_SLUG_PREFIX, SCHEDULE_SLUG_META, ScheduleGroup, ScheduleInterface, isBuiltInScheduleSlug } from "@swivl-mono/types";

/** The built-in groups an operator sees; `agent-default` is internal and never listed. */
const LISTED_BUILT_INS = SCHEDULE_SLUG_META.filter((meta) => meta.slug !== "agent-default");

/** `"Collections Hours"` → `"custom-collections-hours"`. Empty when the name has nothing usable. */
export function slugifyScheduleGroupName(name: string): string {
  const body = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64 - CUSTOM_SCHEDULE_SLUG_PREFIX.length);
  return body ? `${CUSTOM_SCHEDULE_SLUG_PREFIX}${body}` : "";
}

type GroupRow = Pick<ScheduleInterface, "slug" | "label" | "groupId" | "locationId">;

/**
 * Every schedule group an org has: the listed built-ins in their fixed order, then the custom
 * groups found on its `schedule` rows, by label. A custom group's label is its org row's; a
 * group that only has narrower rows (org row deleted by hand) falls back to any row's label.
 */
export function scheduleGroupsFromRows(rows: readonly GroupRow[]): ScheduleGroup[] {
  const builtIns: ScheduleGroup[] = LISTED_BUILT_INS.map((meta) => ({ slug: meta.slug, label: meta.label, builtIn: true }));
  const customBySlug = new Map<string, ScheduleGroup>();
  for (const row of rows) {
    if (isBuiltInScheduleSlug(row.slug)) continue;
    const isOrgRow = row.groupId == null && row.locationId == null;
    const existing = customBySlug.get(row.slug);
    if (!existing || isOrgRow) customBySlug.set(row.slug, { slug: row.slug, label: row.label || row.slug, builtIn: false });
  }
  const custom = [...customBySlug.values()].sort((left, right) => left.label.localeCompare(right.label));
  return [...builtIns, ...custom];
}

/** The label for a slug, whether built-in or custom; the slug itself when unknown. */
export function scheduleGroupLabel(slug: string, groups: readonly ScheduleGroup[]): string {
  return groups.find((group) => group.slug === slug)?.label ?? slug;
}
